import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Landmark, Download, Ban, BellRing } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import BankAccountSelect from "@/components/BankAccountSelect";
import { getErrorMessage } from "@/lib/errors";

import { fmtDate } from "@/lib/format";
import { CompanyField } from "@/components/CompanyField";
import { confirmAction } from "@/lib/dialogs";
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const iso = (d) => d.toISOString().slice(0, 10);
const TONE = {
  amber: "text-amber-600 dark:text-amber-400",
  rose: "text-rose-600 dark:text-rose-400",
  emerald: "text-emerald-600 dark:text-emerald-400",
};
const STATUS_STYLE = {
  PENDING: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  OVERDUE: "bg-rose-500/10 text-rose-600 dark:text-rose-400",
  DEPOSITED: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
};

export default function TdsRegister() {
  const { user } = useAuth();
  const allowed = ["ORG_ADMIN", "ACCOUNTS"].includes(user?.role);
  const { companies } = useCompany() || {};
  const now = new Date();
  const [tab, setTab] = useState("register");
  const [filters, setFilters] = useState({ company_id: "", date_from: iso(new Date(now.getFullYear(), now.getMonth() - 2, 1)), date_to: iso(now), status: "PENDING" });
  const [data, setData] = useState({ rows: [], totals: {} });
  const [challans, setChallans] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const [depositOpen, setDepositOpen] = useState(false);

  useEffect(() => { if (!filters.company_id && companies?.[0]) setFilters((f) => ({ ...f, company_id: companies[0].id })); }, [companies]);

  const params = { company_id: filters.company_id || undefined, date_from: filters.date_from || undefined, date_to: filters.date_to || undefined };
  const load = async () => {
    if (!allowed) return;
    setLoading(true);
    try {
      const [reg, ch] = await Promise.all([
        api.get("/tds/register", { params: { ...params, status: filters.status === "ALL" ? undefined : filters.status } }),
        api.get("/tds/challans"),
      ]);
      setData(reg.data);
      setChallans(ch.data);
      setSelected({});
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (filters.company_id) load(); }, [filters.company_id, filters.status]);

  const pendingRows = data.rows.filter((r) => r.status !== "DEPOSITED");
  const selectedRows = data.rows.filter((r) => selected[r.bill_id]);
  const selectedTotal = selectedRows.reduce((s, r) => s + Number(r.tds_amount), 0);
  const allPendingSelected = pendingRows.length > 0 && pendingRows.every((r) => selected[r.bill_id]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/tds/register/export", { params, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `tds_register_${filters.date_from}_${filters.date_to}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const remindNow = async () => {
    try {
      await api.post("/tds/reminders/run");
      toast.success("Reminder queued for Accounts (email / WhatsApp)");
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const cancelChallan = async (c) => {
    if (!await confirmAction(`Cancel challan ${c.challan_no}? The bank entry is reversed and its TDS goes back to Pending.`)) return;
    try {
      await api.post(`/tds/challans/${c.id}/cancel`);
      toast.success("Challan cancelled");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  if (!allowed) return <EmptyState title="Accounts only" description="TDS deposits are handled by the Accounts team." />;

  return (
    <div className="space-y-6" data-testid="tds-page">
      <PageHeader
        title="Statutory — TDS"
        description="TDS cut from contractors' bills must be paid to the government by the 7th of the next month (30 April for March). Record each deposit here with its challan number. Salary TDS is deposited from Payroll Runs."
        action={<div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={remindNow}><BellRing className="h-4 w-4" /> Send reminder now</Button>
          <Button variant="outline" onClick={exportCsv} data-testid="tds-export-button"><Download className="h-4 w-4" /> Export for CA (CSV)</Button>
        </div>}
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[["To deposit", Number(data.totals.pending || 0) + Number(data.totals.overdue || 0), "amber"], ["Overdue", data.totals.overdue, "rose"], ["Deposited", data.totals.deposited, "emerald"], ["Total deducted", data.totals.all]].map(([label, value, tone]) => (
          <div key={label} className="rounded-xl border border-border bg-card p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
            <p className={`mt-2 font-mono text-lg font-bold ${TONE[tone] || "text-foreground"}`}>{inr(value)}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <CompanyField label="Company" value={filters.company_id} onChange={(v) => setFilters({ ...filters, company_id: v })} triggerClassName="mt-1.5 w-52" required={false} />
        <div><Label>Bills from</Label><Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="mt-1.5" /></div>
        <div><Label>to</Label><Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="mt-1.5" /></div>
        <div>
          <Label>Show</Label>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger className="mt-1.5 w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="PENDING">To deposit</SelectItem>
              <SelectItem value="DEPOSITED">Deposited</SelectItem>
              <SelectItem value="ALL">All</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={load} disabled={loading}>{loading && <Loader2 className="h-4 w-4 animate-spin" />} Show</Button>
        <div className="ml-auto flex gap-2">
          <Button variant={tab === "register" ? "default" : "outline"} size="sm" onClick={() => setTab("register")}>TDS register</Button>
          <Button variant={tab === "challans" ? "default" : "outline"} size="sm" onClick={() => setTab("challans")}>Challans ({challans.filter((c) => c.status === "POSTED").length})</Button>
        </div>
      </div>

      {tab === "register" ? (
        <>
          {selectedRows.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-muted/50 px-3 py-2 text-sm">
              <span>{selectedRows.length} selected · <b className="font-mono">{inr(selectedTotal)}</b></span>
              <Button size="sm" onClick={() => setDepositOpen(true)} data-testid="tds-record-deposit-button"><Landmark className="h-4 w-4" /> Record deposit (challan)</Button>
            </div>
          )}
          <div className="overflow-x-auto rounded-xl border border-border bg-card">
            {!loading && data.rows.length === 0 ? (
              <EmptyState title={filters.status === "PENDING" ? "Nothing to deposit" : "No TDS entries"} description="TDS appears here when a service bill with TDS is posted." />
            ) : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead className="w-8">
                    <input type="checkbox" checked={allPendingSelected} onChange={(e) => setSelected(e.target.checked ? Object.fromEntries(pendingRows.map((r) => [r.bill_id, true])) : {})} />
                  </TableHead>
                  <TableHead>Vendor</TableHead><TableHead>PAN</TableHead><TableHead>Section</TableHead><TableHead>Bill</TableHead>
                  <TableHead className="text-right">Bill amount</TableHead><TableHead className="text-right">TDS</TableHead><TableHead>Due by</TableHead><TableHead>Status</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {data.rows.map((r) => (
                    <TableRow key={r.bill_id} data-testid={`tds-row-${r.bill_number}`}>
                      <TableCell>{r.status !== "DEPOSITED" && <input type="checkbox" checked={Boolean(selected[r.bill_id])} onChange={(e) => setSelected({ ...selected, [r.bill_id]: e.target.checked })} />}</TableCell>
                      <TableCell className="text-foreground">{r.vendor_name}</TableCell>
                      <TableCell className="font-mono text-xs">{r.pan || <span className="font-sans text-rose-500">Missing</span>}</TableCell>
                      <TableCell className="text-xs">{r.section}</TableCell>
                      <TableCell className="text-xs"><div className="font-mono">{r.bill_number}</div><div className="text-muted-foreground">{r.bill_date}</div></TableCell>
                      <TableCell className="text-right font-mono text-xs">{inr(r.taxable)}</TableCell>
                      <TableCell className="text-right font-mono text-xs font-semibold">{inr(r.tds_amount)} <span className="font-normal text-muted-foreground">({Number(r.tds_pct)}%)</span></TableCell>
                      <TableCell className="text-xs">{fmtDate(r.due_date)}</TableCell>
                      <TableCell>
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_STYLE[r.status]}`}>{r.status === "PENDING" ? "To deposit" : r.status === "OVERDUE" ? "Overdue" : "Deposited"}</span>
                        {r.challan_no && <div className="mt-0.5 text-[10px] text-muted-foreground">Challan {r.challan_no} · {r.deposit_date}</div>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          {data.rows.some((r) => !r.pan) && <p className="text-xs text-rose-600 dark:text-rose-400">Some vendors have no PAN. Add it in Procurement → Vendors — without PAN, TDS must be deducted at 20% and the return will be rejected.</p>}
        </>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          {challans.length === 0 ? <EmptyState title="No challans yet" /> : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Deposit date</TableHead><TableHead>Challan no.</TableHead><TableHead>BSR code</TableHead><TableHead>Section</TableHead>
                <TableHead className="text-right">TDS</TableHead><TableHead className="text-right">Interest</TableHead><TableHead>Status</TableHead><TableHead />
              </TableRow></TableHeader>
              <TableBody>
                {challans.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>{fmtDate(c.deposit_date)}</TableCell>
                    <TableCell className="font-mono text-xs">{c.challan_no}</TableCell>
                    <TableCell className="font-mono text-xs">{c.bsr_code || "—"}</TableCell>
                    <TableCell className="text-xs">{c.section || "Mixed"}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{inr(c.tds_amount)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{inr(c.interest)}</TableCell>
                    <TableCell className="text-xs">{c.status}</TableCell>
                    <TableCell className="text-right">{c.status === "POSTED" && <Button size="sm" variant="ghost" onClick={() => cancelChallan(c)} title="Cancel"><Ban className="h-3.5 w-3.5 text-rose-400" /></Button>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      )}

      <DepositDialog open={depositOpen} onOpenChange={setDepositOpen} rows={selectedRows} total={selectedTotal} companyId={filters.company_id} onDone={() => { setDepositOpen(false); load(); }} />
    </div>
  );
}

function DepositDialog({ open, onOpenChange, rows, total, companyId, onDone }) {
  const [form, setForm] = useState({ deposit_date: iso(new Date()), challan_no: "", bsr_code: "", interest: "0", bank_account_id: "", remarks: "" });
  const [saving, setSaving] = useState(false);
  const overdue = useMemo(() => rows.some((r) => form.deposit_date > r.due_date), [rows, form.deposit_date]);
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/tds/challans", { ...form, company_id: companyId, bill_ids: rows.map((r) => r.bill_id), bsr_code: form.bsr_code || null, bank_account_id: form.bank_account_id || null, remarks: form.remarks || null });
      toast.success(`Deposit of ${inr(total)} recorded — bank reduced, TDS Payable cleared`);
      setForm({ deposit_date: iso(new Date()), challan_no: "", bsr_code: "", interest: "0", bank_account_id: "", remarks: "" });
      onDone?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Record TDS deposit</DialogTitle>
          <DialogDescription>Pay the TDS on the income-tax portal first, then enter the challan details here. {rows.length} bill(s), {inr(total)}.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3.5">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Deposit date</Label><Input required type="date" value={form.deposit_date} onChange={set("deposit_date")} className="mt-1.5" /></div>
            <div><Label>Challan serial no.</Label><Input required value={form.challan_no} onChange={set("challan_no")} className="mt-1.5" data-testid="tds-challan-no-input" /></div>
            <div><Label>BSR code</Label><Input value={form.bsr_code} onChange={set("bsr_code")} className="mt-1.5" /></div>
            <div><Label>Interest paid (₹)</Label><Input type="number" step="0.01" min="0" value={form.interest} onChange={set("interest")} className="mt-1.5" /></div>
          </div>
          {overdue && <p className="rounded-lg bg-rose-500/10 p-2 text-xs text-rose-700 dark:text-rose-400">This deposit is after the due date for some entries — interest applies. Enter the interest you paid on the challan.</p>}
          <BankAccountSelect companyId={companyId} value={form.bank_account_id} onChange={(v) => setForm({ ...form, bank_account_id: v })} label="Paid from (bank / cash account)" testId="tds-bank-select" />
          <div><Label>Remarks</Label><Input value={form.remarks} onChange={set("remarks")} className="mt-1.5" /></div>
          <div className="rounded-lg bg-muted/50 p-3 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">TDS</span><span className="font-mono">{inr(total)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Interest</span><span className="font-mono">{inr(form.interest)}</span></div>
            <div className="flex justify-between font-semibold"><span>Paid from bank</span><span className="font-mono">{inr(total + Number(form.interest || 0))}</span></div>
          </div>
          <DialogFooter><Button type="submit" disabled={saving || !rows.length}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Record deposit</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
